import { useShallow } from 'zustand/react/shallow'
import type { View } from '@/lib/api/client'
import type { Step } from '@/services/queries/operation_steps/types'
import { Screenshot } from './Screenshot'
import { useKickViewWorker } from '@/services/hooks/view_worker'
import { useAppStore } from '@/state'
import { useCADPageStore } from '@/state'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils'

export const ScreenshotList = ({
  views,
  steps,
  isLoading,
  isDisabled,
  isReadOnly,
  onClick,
  onRetake,
  onDelete,
}: {
  views: View[]
  steps: Step[]
  isLoading: boolean
  isDisabled?: boolean
  isReadOnly: boolean
  onRetake?: (view: View) => void
  onDelete?: (viewId?: string | null) => void
  onClick: (attrs: {
    view: View
    attachedStep?: Step
    event: React.MouseEvent<HTMLImageElement, MouseEvent>
  }) => void
}) => {
  useKickViewWorker({ kickOnMount: true })

  const allViewUploadsInProgress = useAppStore(
    (state) => state.viewUploadsInProgress,
  )

  const filterScreenshotsByDocumentPageId = useCADPageStore(
    (state) => state.filterScreenshotsByDocumentPageId,
  )

  const retakeViewId = useCADPageStore(
    useShallow((state) => state.retakeViewId),
  )

  const isPanelOpen = useCADPageStore(
    useShallow((state) => state.screenshotPanelOpen),
  )

  const screenshots = views
    .sort((a, b) => {
      const stepA = steps.find((s) => s.id === a.document_page)
      const stepB = steps.find((s) => s.id === b.document_page)
      return (stepA?.order_number || 0) - (stepB?.order_number || 0)
    })
    .filter((view) => {
      return filterScreenshotsByDocumentPageId
        ? view.document_page === filterScreenshotsByDocumentPageId
        : true
    })

  const viewUploadsInProgress = Array.from(
    new Set(
      allViewUploadsInProgress
        .filter((p) => {
          const screenshot = screenshots.find((s) => s.id === p.viewId)
          return screenshot && !screenshot.download_url
        })
        .map((p) => p.viewId),
    ),
  )

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 auto-rows-max gap-4 overflow-y-auto h-full w-full mt-4 pb-16">
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    )
  }

  if (screenshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center space-y-2 mt-10">
        <h1 className="text-md font-semibold text-center">
          No screenshots found.
        </h1>
        <p className="text-sm text-gray-500 text-center">
          Create or select an operation to take a screenshot.
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'grid grid-cols-3 auto-rows-max gap-4 overflow-y-auto h-full w-full mt-4 pb-16',
        {
          'pointer-events-none': isDisabled,
        },
      )}
    >
      {viewUploadsInProgress.map((_, i) => (
        <Skeleton key={i} className="h-64 w-56 border" />
      ))}
      {screenshots.map(
        (view, index) =>
          view.download_url && (
            <Screenshot
              key={view.id}
              index={index}
              view={view}
              steps={steps}
              isReadOnly={isReadOnly}
              isLoading={isDisabled}
              onRetake={onRetake && (() => onRetake(view))}
              onDelete={onDelete && (() => onDelete(view.id))}
              onClick={({ attachedStep, event }) => {
                onClick({ view, attachedStep, event })
              }}
              isFocused={view.id === retakeViewId && isPanelOpen}
            />
          ),
      )}
    </div>
  )
}
